import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'
import ReplyIcon from '@mui/icons-material/Reply'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Tooltip,
  TooltipProps,
  Typography,
  tooltipClasses,
} from '@mui/material'
import {
  DataGrid,
  GridCellModes,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridSortModel,
} from '@mui/x-data-grid'
import { FC, useEffect, useRef, useState } from 'react'
import { CabPropsType, CabType, CategoryType } from '../../api/appAPI'
// import ShareIcon from '@mui/icons-material/Share'
import CheckIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import { styled } from '@mui/styles'
import { useSnackbar } from 'notistack'
import {
  getCabinetPropFromName,
  isEmail,
  saveGridStateInLocalStorage,
} from '../../common/helpers'
import useUnit from '../../hooks/useUnit'
import { EnqueueSnackbarType, Flex, showMessage } from '../common/Helpers'
import { cmToIn } from '../common/UnitComponents'

const TitleCellEditComponent: FC<{
  title: string
  // allTitle: string[]

  submitChange(title: string, isSame: boolean): void
}> = ({ title, submitChange }) => {
  const [value, setValue] = useState(title)

  return (
    <Box display="flex" width={1} alignItems="center">
      <TextField
        // label="Cabinet Title"
        variant="outlined"
        value={value}
        onChange={(e) => {
          setValue(e.target.value.replace(/['"]/, ''))
        }}
        onFocus={(e) => {
          e.target.select()
        }}
        size="small"
        sx={{ width: '100%', ml: 1 }}
      />
      <IconButton
        onClick={() => {
          submitChange(value, value === title)
        }}
      >
        <CheckIcon style={{ color: '#4caf50' }} />
      </IconButton>
    </Box>
  )
}

const TitleCellComponent: FC<{
  title: string

  onEditClick(): void
}> = ({ title, onEditClick }) => {
  return (
    <Box
      display="flex"
      width={1}
      alignItems="center"
      justifyContent="space-between"
    >
      <Typography>{title}</Typography>
      <IconButton
        onClick={() => {
          onEditClick()
        }}
      >
        <EditIcon />
      </IconButton>
    </Box>
  )
}

interface ShareDialogProps {
  cabinetToShare: CabType | undefined
  availableEmails: string[]
  enqueueSnackbar: EnqueueSnackbarType

  handleCloseDialog(): void
  shareCabinet(
    cabinetId: number,
    isReference: boolean,
    emails: string[],
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const ShareDialog: FC<ShareDialogProps> = ({
  cabinetToShare,
  availableEmails,
  enqueueSnackbar,
  handleCloseDialog,
  shareCabinet,
}) => {
  const [isReference, setIsReference] = useState(false)
  const [emailsToShare, setEmailsToShare] = useState<string[]>([])
  const [newEmail, setNewEmail] = useState('')

  const handleShareConfirmClick = () => {
    if (cabinetToShare) {
      shareCabinet(
        cabinetToShare.id,
        isReference,
        emailsToShare,
        enqueueSnackbar
      )
      handleCloseDialog()
    }
  }

  const handleNewEmailBlur = () => {
    if (newEmail) {
      if (!isEmail(newEmail)) {
        showMessage(enqueueSnackbar, 'Email is invalid')
        return
      }

      if (!emailsToShare.includes(newEmail)) {
        setEmailsToShare([...emailsToShare, newEmail])
        setNewEmail('')
      }
    }
  }

  const handleCompanyEmailClick = (email: string) => {
    if (!emailsToShare.includes(email)) {
      setEmailsToShare([...emailsToShare, email])
    }
  }

  return (
    <Dialog open={!!cabinetToShare} onClose={handleCloseDialog}>
      <DialogTitle>Share Cabinet</DialogTitle>
      {!!cabinetToShare && (
        <DialogContent dividers sx={{ display: 'flex', gap: '20px' }}>
          <Box
            sx={{ width: 250, borderRight: '1px solid #e0e0e0', pr: '20px' }}
          >
            <Typography>{cabinetToShare.title}</Typography>
            <img src={cabinetToShare.image} alt="Cabinet Preview" width={200} />
          </Box>
          <Box sx={{ width: 400 }}>
            <Typography>Emails</Typography>

            <Box
              component="ul"
              px={1}
              my={1}
              style={{
                display: 'flex',
                justifyContent: 'center',
                columnGap: 10,
                rowGap: 10,
                flexWrap: 'wrap',
                listStyle: 'none',
              }}
            >
              {emailsToShare.map((email) => (
                <li key={email}>
                  <Chip
                    size="small"
                    label={email}
                    onDelete={() => {
                      setEmailsToShare(emailsToShare.filter((e) => e !== email))
                    }}
                    // color={email.id === props.defaultMaterialId.value ? 'primary' : undefined}
                  />
                </li>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: '5px', mt: 2 }}>
              <TextField
                sx={{ width: '50%' }}
                label="Email"
                size="small"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onBlur={handleNewEmailBlur}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') handleNewEmailBlur()
                }}
              />
              <FormControl sx={{ width: '50%' }} size="small">
                <InputLabel>Company Emails</InputLabel>
                <Select label="Company Emails" value="">
                  {availableEmails.map((email) => (
                    <MenuItem
                      onClick={() => handleCompanyEmailClick(email)}
                      value={email}
                      key={email}
                    >
                      {email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Divider sx={{ mt: 2 }} />
            <FormControlLabel
              label="Share as copy"
              control={
                <Checkbox
                  checked={!isReference}
                  onChange={(e) => setIsReference(!e.target.checked)}
                />
              }
            />
          </Box>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button onClick={handleShareConfirmClick}>Share</Button>
      </DialogActions>
    </Dialog>
  )
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -20],
          },
        },
      ],
    }}
  />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
})

interface IPropsNameCell {
  value: string
}

export const CellWithTooltip: FC<IPropsNameCell> = (props) => {
  const ref = useRef<HTMLDivElement>(null)

  return (
    <Box ref={ref} width={1}>
      <CustomTooltip
        title={
          (ref.current?.parentElement?.scrollWidth || 0) >
          (ref.current?.parentElement?.clientWidth || 0)
            ? props.value
            : ''
        }
      >
        <div>{props.value}</div>
      </CustomTooltip>
    </Box>
  )
}

interface IProps {
  open: boolean
  allCabinetsList: CabType[]
  cabinetsList: CabType[]
  userCabinetsGridVisibilityModel: GridColumnVisibilityModel | undefined
  userCabinetsGridSortModel: GridSortModel | undefined
  userCabinetsGridFilterModel: GridFilterModel | undefined
  companyEmails: string[]
  categoriesList: CategoryType[]

  setOpen(value: boolean): void
  loadCabinet(cabinet: CabType): void
  copyCabinet(id: number): void
  deleteCabinet(id: number): void
  setUserCabinetsGridVisibilityModel(
    model: GridColumnVisibilityModel | undefined
  ): void
  setUserCabinetsGridSortModel(model: GridSortModel | undefined): void
  setUserCabinetsGridFilterModel(model: GridFilterModel | undefined): void
  editCabinet(
    id: number,
    cabProps: CabPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  shareCabinet(
    cabinetId: number,
    isReference: boolean,
    emails: string[],
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const CabinetCatalogDialog: FC<IProps> = (props) => {
  const [tab, setTab] = useState<'my' | 'all'>('my')
  const [cabinetToShare, setCabinetToShare] = useState<CabType | undefined>(
    undefined
  )
  const [editRowsModel, setEditRowsModel] = useState({})
  const [imageSrc, setImageSrc] = useState('')
  const [pos, setPos] = useState({ left: 0, top: 0 })
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // const [bigMode, setBigMode] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const { unit } = useUnit()

  useEffect(() => {
    saveGridStateInLocalStorage(
      {
        visibilityModel: props.userCabinetsGridVisibilityModel,
        sortModel: props.userCabinetsGridSortModel,
        filterModel: props.userCabinetsGridFilterModel,
      },
      'userCabinets'
    )
  }, [
    props.userCabinetsGridVisibilityModel,
    props.userCabinetsGridSortModel,
    props.userCabinetsGridFilterModel,
  ])

  useEffect(() => {
    if (props.open) {
      for (const cab1 of props.cabinetsList) {
        let needBreak = false
        for (const cab2 of props.cabinetsList) {
          if (cab1.title === cab2.title && cab1.id !== cab2.id) {
            showMessage(
              enqueueSnackbar,
              'There are two cabinet with title - ' +
                cab1.title +
                ', rename one!',
              'warning'
            )
            needBreak = true
            break
          }
        }

        if (needBreak) break
      }
    }
  }, [props.cabinetsList, props.open])

  const handleCopyClick = (id: number) => {
    props.copyCabinet(id)
  }

  const handleDeleteClick = (id: number) => {
    if (window.confirm('Are you sure?')) {
      props.deleteCabinet(id)
    }
  }

  const handleShareClick = (id: number) => {
    setCabinetToShare(
      (tab === 'my' ? props.cabinetsList : props.allCabinetsList).find(
        (c) => c.id === id
      )
    )
  }

  const handleDialogClose = () => {
    props.setOpen(false)
  }

  const loadCabinet = (id: number) => {
    const cab = (
      tab === 'my' ? props.cabinetsList : props.allCabinetsList
    ).find((c) => c.id === id)

    if (cab) {
      props.loadCabinet(cab)
      props.setOpen(false)
    }
  }

  return (
    <>
      <Dialog
        open={props.open}
        onClose={handleDialogClose}
        maxWidth={false}
        sx={{ '& .MuiDialog-paper': { width: '80%', height: '80%' } }}
      >
        <DialogTitle sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          Cabinets List
          {props.allCabinetsList.length > 0 && (
            <Flex centerY>
              <Box>My</Box>
              <Switch
                checked={tab === 'all'}
                onChange={(e) => setTab(e.target.checked ? 'all' : 'my')}
              />
              <Box>All</Box>
            </Flex>
          )}
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DataGrid
            style={{ width: '100%', maxHeight: '100%', overflowY: 'auto' }}
            columns={[
              { field: 'id' },
              {
                field: 'date',
                headerName: 'Last Modified',
                type: 'datetime',
                minWidth: 160,
              },
              ...(tab === 'all'
                ? [{ field: 'creator', headerName: 'Creator', width: 150 }]
                : []),
              {
                field: 'image',
                headerName: '#',
                minWidth: 70,
                renderCell: (params: GridRenderCellParams) => (
                  <img
                    src={params.value?.toString()}
                    width={50}
                    alt="Cabinet Preview"
                    onMouseEnter={(e) => {
                      // setBigMode(true)
                      if (
                        params.value &&
                        params.value !== 'data:image/png;base64,'
                      ) {
                        setImageSrc(params.value?.toString() || '')
                        setAnchorEl(e.currentTarget)

                        const { top, left } =
                          e.currentTarget.getBoundingClientRect()
                        setPos({ top, left })
                      }
                    }}

                    // onMouseLeave={() => {
                    //     // setAnchorEl(null)
                    //     setImageSrc('')
                    // }}
                  />
                ),
              },
              {
                field: 'title',
                headerName: 'Title',
                width: 200,
                editable: true,
                renderCell:
                  tab === 'all'
                    ? undefined
                    : (params: GridRenderCellParams) => (
                        <TitleCellComponent
                          title={params.value as string}
                          onEditClick={() => {
                            let model: { [key: string]: any } = {}
                            model[params.id] = {
                              title: { mode: GridCellModes.Edit },
                            }
                            setEditRowsModel(model)
                          }}
                        />
                      ),
                renderEditCell:
                  tab === 'all'
                    ? undefined
                    : (params: GridRenderEditCellParams) => (
                        <TitleCellEditComponent
                          title={params.value as string}
                          submitChange={(title: string, isSame: boolean) => {
                            if (!isSame) {
                              // props.setUserRole(params.id, role, enqueueSnackbar)
                              if (
                                props.cabinetsList.every(
                                  (c) => c.title !== title
                                )
                              ) {
                                const cab = props.cabinetsList.find(
                                  (c) => c.id === parseInt(params.id.toString())
                                )

                                if (cab) {
                                  cab.title = title
                                  const { id, ...cabProps } = cab
                                  props.editCabinet(
                                    id,
                                    cabProps,
                                    enqueueSnackbar
                                  )
                                }
                              } else {
                                showMessage(
                                  enqueueSnackbar,
                                  'Cabinet title must be unique'
                                )
                                return
                              }
                            }

                            setEditRowsModel({
                              [params.id]: {
                                title: { mode: GridCellModes.View },
                              },
                            })
                          }}
                        />
                      ),
              },
              // { field: 'name', headerName: 'Name', width: 350 },
              {
                field: 'name',
                headerName: 'Name',
                width: 550,
                renderCell: (params: GridRenderCellParams) => (
                  <CellWithTooltip value={params.value?.toString() || ''} />
                ),
              },
              {
                field: 'additional',
                headerName: 'Additional Items',
                width: 200,
              },
              { field: 'type', headerName: 'Type', width: 110 },
              { field: 'cornerType', headerName: 'Corner Type', width: 110 },
              { field: 'categoryName', headerName: 'Category', width: 150 },
              {
                field: 'microvellumName',
                headerName: 'Microvellum Name',
                width: 150,
              },
              {
                field: 'width',
                headerName: 'Width',
                type: 'number',
                width: 130,
              },
              {
                field: 'height',
                headerName: 'Height',
                type: 'number',
                width: 130,
              },
              {
                field: 'depth',
                headerName: 'Depth',
                type: 'number',
                width: 130,
              },
              {
                field: 'backWidth',
                headerName: 'Back Width',
                type: 'number',
                width: 130,
              },
              {
                field: 'sectionsCount',
                headerName: 'Sections Count',
                type: 'number',
                width: 150,
              },
              {
                field: 'doorsCount',
                headerName: 'Doors Count',
                type: 'number',
                width: 150,
              },
              {
                field: 'drawersCount',
                headerName: 'Drawers Count',
                type: 'number',
                width: 150,
              },
              {
                field: 'actions',
                headerName: 'Actions',
                filterable: false,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation() // don't select this row after clicking
                    }}
                  >
                    <IconButton
                      edge="end"
                      onClick={() => {
                        handleCopyClick(parseInt(params.id.toString()))
                      }}
                    >
                      <Tooltip title="Copy">
                        <CopyIcon />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        handleDeleteClick(parseInt(params.id.toString()))
                      }}
                    >
                      <Tooltip title="Delete">
                        <DeleteIcon />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        handleShareClick(parseInt(params.id.toString()))
                      }}
                    >
                      <Tooltip title="Share">
                        <ReplyIcon sx={{ transform: 'scaleX(-1)' }} />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        loadCabinet(parseInt(params.id.toString()))
                      }}
                    >
                      <Tooltip title="Load">
                        <EditIcon />
                      </Tooltip>
                    </IconButton>
                  </Box>
                ),
                width: 150,
              },
            ]}
            // hideFooterPagination
            rows={(tab === 'my'
              ? props.cabinetsList
              : props.allCabinetsList
            ).map((cabinet) => {
              const [_name, microvellumName] = cabinet.name.split('#')
              const [, categoryId] = _name.split('$')

              const categoryName = categoryId
                ? props.categoriesList.find(
                    (c) => c.id === parseInt(categoryId)
                  )?.name || ''
                : ''

              let width = parseFloat(
                getCabinetPropFromName(cabinet.name, 'width') as string
              )
              width = unit === 'cm' ? width : cmToIn(width)
              let height = parseFloat(
                getCabinetPropFromName(cabinet.name, 'height') as string
              )
              height = unit === 'cm' ? height : cmToIn(height)
              let depth = parseFloat(
                getCabinetPropFromName(cabinet.name, 'depth') as string
              )
              depth = unit === 'cm' ? depth : cmToIn(depth)
              let backWidth = parseFloat(
                getCabinetPropFromName(cabinet.name, 'backWidth') as string
              )
              backWidth = unit === 'cm' ? backWidth : cmToIn(backWidth)

              return {
                id: cabinet.id,
                date: new Date(cabinet.date),
                image: cabinet.image,
                title: cabinet.title,
                name: cabinet.name,
                creator: cabinet.creator,
                additional: cabinet.additional,
                type: getCabinetPropFromName(cabinet.name, 'type'),
                cornerType: getCabinetPropFromName(cabinet.name, 'cornerType'),
                categoryName,
                microvellumName,
                width: isNaN(width) ? '' : width,
                height: isNaN(height) ? '' : height,
                depth: isNaN(depth) ? '' : depth,
                backWidth: isNaN(backWidth) ? '' : backWidth,
                sectionsCount: getCabinetPropFromName(
                  cabinet.name,
                  'sectionsCount'
                ),
                doorsCount: getCabinetPropFromName(cabinet.name, 'doorsCount'),
                drawersCount: getCabinetPropFromName(
                  cabinet.name,
                  'drawersCount'
                ),
                // description: cabinet.description,
                // unitPrice: cabinet.unitPrice,
                // markup: props.markups.find(m => m.id === cabinet.markupId)?.name || cabinet.markupId,
                // materialBased: cabinet.materialsIds.length > 0 ? '✓' : '',
                // materialIds: cabinet.materialsIds.join(','),
                // defaultMaterialId: cabinet.defaultMaterialId,
                // children: cabinet.childrenIds.join(','),
              }
            })}
            // onRowClick={(param) => {
            //     props.setActiveHardwareId(parseInt(param.id.toString()))
            // }}
            onRowDoubleClick={(param) => {
              loadCabinet(parseInt(param.id.toString()))
            }}
            columnVisibilityModel={props.userCabinetsGridVisibilityModel}
            onColumnVisibilityModelChange={(model) => {
              props.setUserCabinetsGridVisibilityModel(model)
            }}
            sortModel={props.userCabinetsGridSortModel}
            onSortModelChange={(model) => {
              if (
                JSON.stringify(model) !==
                JSON.stringify(props.userCabinetsGridSortModel)
              )
                props.setUserCabinetsGridSortModel(model)
            }}
            filterModel={props.userCabinetsGridFilterModel}
            onFilterModelChange={(model) => {
              if (
                JSON.stringify(model) !==
                JSON.stringify(props.userCabinetsGridFilterModel)
              )
                props.setUserCabinetsGridFilterModel(model)
            }}
            showCellVerticalBorder
            cellModesModel={editRowsModel}
            // editRowsModel={editRowsModel}
            // rowSelectionModel={props.activeHardwareId}
          />
        </DialogContent>
      </Dialog>
      <ShareDialog
        cabinetToShare={cabinetToShare}
        availableEmails={props.companyEmails}
        enqueueSnackbar={enqueueSnackbar}
        handleCloseDialog={() => {
          setCabinetToShare(undefined)
        }}
        shareCabinet={props.shareCabinet}
      />
      <Popover
        open={imageSrc !== ''}
        anchorEl={anchorEl}
        anchorReference="anchorPosition"
        onClose={() => {
          setImageSrc('')
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        anchorPosition={{
          top: pos.top - 20,
          left: pos.left + 25,
        }}
      >
        <img width={200} alt="cabinet" src={imageSrc} />
      </Popover>
    </>
  )
}

export default CabinetCatalogDialog
